import { useState } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import ModalShell from "../../../ui/overlays/ModalShell";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { changeThreadTitle } from "../data/settingsChanges";
import { notifyThreadListChanged } from "../data/threadListEvents";

/** The server's limit on a title (`Title`). */
const MAXIMUM_TITLE_CHARACTERS = 200;

interface RenameThreadDialogProps {
  threadId: string;
  title: string;
  /** The last entry the host saw: the rename is conditional on it, retried once on the newest. */
  lastEntryNumber: number;
  onClose: () => void;
}

/** The host renames the Thread, for everyone in it, so the title fits the work (F68). */
export default function RenameThreadDialog({ threadId, title, lastEntryNumber, onClose }: RenameThreadDialogProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(title);
  const { state, submit } = useSubmission();
  const trimmed = value.trim();
  const unchanged = trimmed === "" || trimmed === title;
  return (
    <ModalShell title={t("threads.rename.title")} onClose={onClose} size="md">
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (unchanged) return;
          submit(async () => {
            await changeThreadTitle(threadId, lastEntryNumber, trimmed);
            notifyThreadListChanged();
            onClose();
          });
        }}
      >
        <Field label={t("threads.rename.label")}>
          {(control) => (
            <input {...control} autoFocus maxLength={MAXIMUM_TITLE_CHARACTERS} value={value} onChange={(event) => { setValue(event.target.value); }} className={FORM_CONTROL_CLASS} />
          )}
        </Field>
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
        <div className="flex justify-end gap-2">
          <AppButton variant="secondary" size="sm" onClick={onClose}>{t("common.cancel")}</AppButton>
          <AppButton type="submit" variant="primary" size="sm" disabled={unchanged || state.status === "submitting"}>{t("threads.rename.save")}</AppButton>
        </div>
      </form>
    </ModalShell>
  );
}
